import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

const authSchema = z.object({
  email: z.string()
    .email('Please enter a valid email address')
    .min(1, 'Email is required'),
  password: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  rememberMe: z.boolean().optional()
});

type AuthForm = z.infer<typeof authSchema>;

const AuthPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, isAuthenticated, isAdmin, isLoading, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError: setFormError,
  } = useForm<AuthForm>({
    resolver: zodResolver(authSchema),
    mode: 'onBlur'
  });

  useEffect(() => {
    console.log('Auth state changed:', { isAuthenticated, isAdmin, from });
    if (isAuthenticated) {
      const redirectPath = isAdmin ? '/admin' : from;
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, isAdmin, navigate, from]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const handleAuth = async (data: AuthForm) => {
    console.log('Handling auth submission:', { email: data.email });
    try {
      const result = await signIn(data.email, data.password);
      console.log('Sign in result:', result);
      
      if (!result.success) {
        if (result.error.toLowerCase().includes('password')) {
          setFormError('password', { message: result.error });
        } else if (result.error.toLowerCase().includes('email')) {
          setFormError('email', { message: result.error });
        } else {
          setFormError('root.serverError', { message: result.error });
        }
      }
    } catch (err) {
      console.error('Auth error:', err);
      setFormError('root.serverError', { 
        message: 'An unexpected error occurred. Please try again.' 
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-[#00A86B] bg-opacity-10">
            <Lock className="h-6 w-6 text-[#00A86B]" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Sign in to TranslateNow
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Access your translation workspace
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(handleAuth)}>
          {authError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md text-sm">
              {authError}
            </div>
          )}

          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                disabled={isSubmitting || isLoading}
                className={`appearance-none relative block w-full px-3 py-2 border ${
                  errors.email 
                    ? 'border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 dark:border-gray-700 focus:ring-[#00A86B] focus:border-[#00A86B]'
                } dark:bg-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:z-10 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed`}
                {...register('email')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  disabled={isSubmitting || isLoading}
                  className={`appearance-none relative block w-full px-3 py-2 border ${
                    errors.password 
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 dark:border-gray-700 focus:ring-[#00A86B] focus:border-[#00A86B]'
                  } dark:bg-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:z-10 sm:text-sm pr-10 disabled:opacity-50 disabled:cursor-not-allowed`}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  disabled={isSubmitting || isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="flex items-center">
              <input
                id="remember-me"
                type="checkbox"
                disabled={isSubmitting || isLoading}
                className="h-4 w-4 text-[#00A86B] focus:ring-[#00A86B] border-gray-300 rounded disabled:opacity-50"
                {...register('rememberMe')}
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                Remember me
              </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#00A86B] hover:bg-[#007A4D] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#00A86B] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {(isSubmitting || isLoading) ? (
                <>
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </span>
                  Signing in...
                </>
              ) : (
                <>
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <Lock className="h-4 w-4 text-[#00D88A] group-hover:text-white transition-colors duration-200" />
                  </span>
                  Sign in
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthPage;